import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ArrowUpRight, ChevronRight, Compass, ListChecks, Replace, Search } from 'lucide-react';
import { PageHeader } from '../components/common/States';
import { LaneDot, PriorityBadge } from '../components/items/Tags';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useMeta } from '../hooks/queries';
import { api } from '../lib/api';
import { fmtDate, fmtEur, fmtInt } from '../lib/format';
import { cn } from '../lib/utils';

const LABELS = [
  { key: 'diagnosis', group: 'by_diagnosis', param: 'diagnosis', step: '1', question: 'Why is it stuck?' },
  { key: 'exit_lane', group: 'by_lane', param: 'exit_lane', step: '2', question: 'How do we get it out?' },
  { key: 'priority', group: 'by_priority', param: 'priority', step: '3', question: 'What do we do first?' },
];

function Section({ id, eyebrow, title, intro, children }) {
  return (
    <section id={id} className="scroll-mt-6 space-y-4">
      <div className="max-w-3xl space-y-1.5">
        {eyebrow && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{eyebrow}</p>}
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {intro && <p className="text-sm leading-relaxed text-muted-foreground">{intro}</p>}
      </div>
      {children}
    </section>
  );
}

/** Live count + value for one label value; clicking opens those products on Explore. */
function Count({ stat, onClick }) {
  if (!stat) return <span className="text-xs text-muted-foreground">0 products</span>;
  return (
    <button type="button" onClick={onClick} className="group inline-flex items-center gap-1 whitespace-nowrap text-xs tabular-nums text-muted-foreground hover:text-foreground">
      {fmtInt(stat.n)} products · {fmtEur(stat.value)}
      <ArrowUpRight className="h-3 w-3 opacity-0 transition group-hover:opacity-100" />
    </button>
  );
}

function ValueRow({ k, v, i, stat, onOpen }) {
  return (
    <li className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-6">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
          <span className="w-4 text-xs tabular-nums text-muted-foreground">{i + 1}</span>
          {k === 'exit_lane' && <LaneDot lane={v.value} />}
          {k === 'priority' ? <PriorityBadge priority={v.value} /> : v.value}
        </div>
        <p className="pl-6 text-sm text-muted-foreground">{v.plain}</p>
        {v.how && (
          <p className="pl-6 text-sm">
            <span className="font-medium">How: </span>
            <span className="text-muted-foreground">{v.how}</span>
          </p>
        )}
        <details className="group pl-6">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ChevronRight className="h-3 w-3 transition group-open:rotate-90" /> Exact rule
          </summary>
          <p className="mt-1 font-mono text-[11px] leading-relaxed text-foreground/70">{v.rule}</p>
        </details>
      </div>
      <div className="pl-6 sm:pl-0 sm:pt-0.5">
        <Count stat={stat} onClick={onOpen} />
      </div>
    </li>
  );
}

function LabelCard({ label, def, stats, go }) {
  const byKey = Object.fromEntries((stats || []).map((s) => [s.key, s]));
  return (
    <div id={label.key} className="scroll-mt-6 overflow-hidden rounded-lg border bg-card">
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background">{label.step}</span>
          <h3 className="text-sm font-semibold">
            {def.title} <span className="font-normal text-muted-foreground">· {label.question}</span>
          </h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{def.plain}</p>
      </div>
      <ul className="divide-y">
        {def.values.map((v, i) => (
          <ValueRow key={v.value} k={label.key} v={v} i={i} stat={byKey[v.value]} onOpen={() => go({ [label.param]: v.value })} />
        ))}
      </ul>
    </div>
  );
}

const PAGES = [
  { icon: Search, title: 'Explore', to: '/explore', tour: '/explore?tour=1', text: 'Browse all old stock. Search with words, a code or a photo, or ask AI in your own words. Filter on the labels above.' },
  { icon: Replace, title: 'Substitute', to: '/substitute', tour: '/substitute?tour=1', text: 'A client asks for a product? Drop a photo or describe it, and Toxy finds what we already have in stock. AI picks the best and writes the message.' },
  { icon: ListChecks, title: 'Shortlists', to: '/shortlists', text: 'Collect products for a project, set quantities, check live stock in Odoo and export to Excel.' },
];

const CARD_TERMS = [
  ['Price', 'The shop price today. Red when it is already discounted, with the normal price (RRP) crossed out.'],
  ['Stock · Sold 6/12M', 'How many pieces we have free, and how many we sold in the last 6 and 12 months (all channels). Few sales and a lot of stock means it will take years.'],
  ['Cost', 'What we paid per piece, without VAT.'],
  ['Min. (minimum price)', 'The lowest shop price without losing money: (cost + €10 handling) × 1.21 VAT. Use it as your floor when you negotiate.'],
  ['Age', 'How long the pieces have been in our warehouse on average (oldest pieces count first).'],
  ['Product info %', 'How complete the product page is: photos, descriptions in all languages, specs. Low means customers cannot find or judge it.'],
  ['Ask supplier', 'We bought it recently from a supplier we still work with: worth asking about a return or exchange. It is a hint, not a confirmed right to return.'],
];

export default function Guide() {
  const navigate = useNavigate();
  const { data: meta } = useMeta();
  const { data } = useQuery({ queryKey: ['overview'], queryFn: api.overview });
  const defs = meta?.definitions;
  const t = data?.totals || {};
  const lots = data?.by_lane?.find((g) => g.key === 'B2B project lot');
  const go = (params) => navigate(`/explore?${new URLSearchParams(params).toString()}`);

  return (
    <div className="w-full px-3 pb-20 pt-4 sm:px-6 lg:pt-6">
      <div className="mx-auto max-w-5xl space-y-12">
        <PageHeader
          title="How Toxy works"
          description={meta?.snapshot_date ? `Numbers from the snapshot of ${fmtDate(meta.snapshot_date)}, updated every night.` : 'Old stock first: what it is, how we label it, and how to use Toxy.'}
        />

        {/* goal */}
        <section className="overflow-hidden rounded-xl border bg-card">
          <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md:p-8">
            <div className="space-y-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">The goal</p>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Old stock first. Let’s get rid of it together.</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Our warehouse holds products that have been there for months and hardly sell. That is money stuck on a shelf, and
                space we pay for. Toxy shows all of it in one place, explains <b className="text-foreground">why</b> each product is stuck and
                the most realistic <b className="text-foreground">way out</b>.
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                The ask for B2B and projects: <b className="text-foreground">before you order something new for a client, check Toxy first.</b>{' '}
                In stock means it ships today, often at a good price, and old stock finally leaves the warehouse.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={() => navigate('/substitute?tour=1')}>
                  <Replace className="h-4 w-4" /> Find a substitute
                </Button>
                <Button variant="outline" onClick={() => navigate('/explore?tour=1')}>
                  <Compass className="h-4 w-4" /> Take the tour
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 self-center">
              {[
                ['Stuck in old stock', t.value != null ? fmtEur(t.value) : null, t.n != null ? `${fmtInt(t.n)} products` : null],
                ['Pieces', t.units != null ? fmtInt(t.units) : null, 'free in the warehouse'],
                ['Project lots', lots ? fmtEur(lots.value) : null, lots ? `${fmtInt(lots.n)} products with 4+ pieces` : null],
                ['No sale in 6 months', t.no_sale_6m_n != null ? fmtInt(t.no_sale_6m_n) : null, 'products'],
              ].map(([label, value, sub]) => (
                <div key={label} className="rounded-lg border bg-background px-3 py-2.5">
                  <div className="text-xs text-muted-foreground">{label}</div>
                  {value != null ? <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div> : <Skeleton className="mt-1 h-6 w-20" />}
                  {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* what is old stock */}
        <Section
          id="old-stock"
          eyebrow="Step 0"
          title="What counts as old stock?"
          intro={`${defs?.universe?.plain || ''} Products on the second-chance (refurbished/returned) list and packaging are left out: they are handled elsewhere.`}
        >
          <div className="grid gap-3 md:grid-cols-3">
            {(defs?.universe?.values || [null, null, null]).map((v, i) =>
              v ? (
                <div key={v.value} className="flex flex-col rounded-lg border bg-card p-4">
                  <h3 className="text-sm font-semibold">{v.value}</h3>
                  <p className="mt-1 flex-1 text-sm text-muted-foreground">{v.plain}</p>
                  <details className="group mt-2">
                    <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                      <ChevronRight className="h-3 w-3 transition group-open:rotate-90" /> Exact rule
                    </summary>
                    <p className="mt-1 font-mono text-[11px] leading-relaxed text-foreground/70">{v.rule}</p>
                  </details>
                  <div className="mt-3 border-t pt-2">
                    <Count stat={data?.by_universe?.find((g) => g.key === v.value)} onClick={() => go({ universe: v.value })} />
                  </div>
                </div>
              ) : (
                <Skeleton key={i} className="h-36 rounded-lg" />
              )
            )}
          </div>
        </Section>

        {/* three labels */}
        <Section
          id="labels"
          eyebrow="Steps 1 to 3"
          title="Every product gets three labels"
          intro="Toxy looks at sales, stock, price, size and the product page of every product, and gives it three labels. Each label comes with one sentence with the product’s own numbers, which you see when you hover the label on a card."
        >
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center">
            {LABELS.map((l, i) => (
              <div key={l.key} className="contents">
                <a href={`#${l.key}`} className="rounded-lg border bg-card px-4 py-3 transition hover:border-foreground/20">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background">{l.step}</span>
                    <span className="text-sm font-semibold">{defs?.[l.key]?.title || '…'}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{l.question}</p>
                </a>
                {i < LABELS.length - 1 && <ArrowRight className="mx-auto hidden h-4 w-4 text-muted-foreground sm:block" />}
              </div>
            ))}
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            For each label, Toxy checks the options from top to bottom and picks the <b className="text-foreground">first one that fits</b>. Click the numbers to see those products.
          </p>
          <div className="space-y-5">
            {LABELS.map((l) =>
              defs?.[l.key] ? <LabelCard key={l.key} label={l} def={defs[l.key]} stats={data?.[l.group]} go={go} /> : <Skeleton key={l.key} className="h-72 rounded-lg" />
            )}
          </div>
        </Section>

        {/* reading a card */}
        <Section id="card" title="The numbers on a product card" intro="Hover any number in the app for the same explanation.">
          <dl className="divide-y overflow-hidden rounded-lg border bg-card">
            {CARD_TERMS.map(([term, text]) => (
              <div key={term} className="grid gap-1 px-4 py-3 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-6">
                <dt className="text-sm font-medium">{term}</dt>
                <dd className="text-sm text-muted-foreground">{text}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* how to use */}
        <Section id="use" title="How to use Toxy" intro="Three pages. Each has a one-minute guided tour.">
          <div className="grid gap-3 md:grid-cols-3">
            {PAGES.map(({ icon: Icon, title, to, tour, text }) => (
              <div key={title} className="flex flex-col rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Icon className="h-4 w-4" /> {title}
                </div>
                <p className="mt-1 flex-1 text-sm text-muted-foreground">{text}</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigate(to)}>
                    Open
                  </Button>
                  {tour && (
                    <Button size="sm" variant="ghost" onClick={() => navigate(tour)}>
                      <Compass className="h-3.5 w-3.5" /> Tour
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <section className={cn('rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground')}>
          <b className="text-foreground">Where the data comes from.</b> Stock, purchase prices and sales from Odoo; webshop prices, photos and
          product info from Shopify and our PIM; ad views from Google Ads; competitor prices from our daily price scrape. Everything is recalculated
          every night. Before promising a quantity to a client, use <b className="text-foreground">Check live stock</b> on the product page: it reads Odoo directly.
        </section>
      </div>
    </div>
  );
}
